/**
 * Tilemap-luminance enum used by Zone 2 in-world DOM overlays (dialogue
 * panel, name placards, sign popups, in-battle dialogue, faint dialogue)
 * to pick the right glass material variant for the underlying region.
 *
 * Lands on the body element as `dialog-luma-light` / `-mid` / `-dark`
 * when an overlay mounts, then unsets on unmount. CSS variants in
 * tokens.css adjust the glass material's fill alpha + saturation +
 * specular intensity per tier — same overlay reads markedly different
 * over warm Pallet vs dark Viridian Forest.
 *
 * Body class lands on overlay MOUNT only. Dialogue blocks walking
 * (joystick.setExternallySuppressed) so a mid-dialogue region cross
 * is impossible by construction — no need to react to region changes
 * while the overlay is up.
 *
 * Region names are the canonical short form used by the seamless-world
 * mega-map (e.g. "PalletTown", "Route1"); interior maps keep their own
 * per-map runtime.json + region name (e.g. "PewterCity_Gym").
 *
 * Unmapped regions default to `'mid'` — the most forgiving variant
 * with moderate glass alpha. Interior buildings (PlayersHouse, OaksLab,
 * PokemonCenter) intentionally fall through to that default; they're
 * neutral indoor lighting that the 'mid' variant reads well over.
 *
 * Couples to TODOs #26 (per-region atmospheric tints). When #26 lands,
 * this table migrates from a hardcoded source-of-truth to metadata
 * read from world.runtime.json region entries.
 */

export type MapLuminance = 'light' | 'mid' | 'dark';

const LUMINANCE: Record<string, MapLuminance> = {
  // Daytime exteriors — sun-soaked tilemap, warm/light average pixels.
  PalletTown: 'light',
  Route1: 'light',
  ViridianCity: 'light',

  // Mid-luminance exteriors + the canopy entrances to Viridian Forest.
  // Route 2's threshold sits at the forest boundary — moderate canopy
  // shade but not yet dark interior. Pewter City is overcast/grey
  // tilemap relative to Pallet/Route 1.
  PewterCity: 'mid',
  PewterCity_Gym: 'mid',
  Route2_ViridianForest_NorthEntrance: 'mid',
  Route2_ViridianForest_SouthEntrance: 'mid',

  // Dark interiors — deep canopy / caves. Mt. Moon + future caves
  // extend this list as they ship.
  ViridianForest: 'dark',
};

/**
 * Resolve a region name to its luminance tier. Returns `'mid'` for any
 * unmapped region — interior buildings and any post-v0.1 regions that
 * haven't been explicitly tiered yet fall through here.
 */
export function luminanceFor(region: string): MapLuminance {
  return LUMINANCE[region] ?? 'mid';
}
